import React, { useEffect, useState } from 'react';
import { Col, Descriptions, Card, Tag, Row, Table, Button, notification, Alert } from 'antd';
import {ClockCircleOutlined} from "@ant-design/icons";
import Spinner from "../../../spinner";
import { billingServices } from "../../../../services/billing.services";
import ModalDepositHistory from "../../../modals/deposit-history";
import ModalSubscriptionHistory from "../../../modals/subscription-history";
import ModalInvoicesHistory from "../../../modals/invoices-history";
import i18n from "../../../../i18n";
import moment from "moment";

export default function SubscriptionPage() {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [deposit, setDeposit] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [isShowDepositHistory, setIsShowDepositHistory] = useState(false);
  const [isShowSubscriptionHistory, setIsShowSubscriptionHistory] = useState(false);
  const [isShowInvoicesHistory, setIsShowInvoicesHistory] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Безопасное извлечение payload из ответа API: { status, data } -> data
  function getPayload(response) {
    if (response && response.data && response.data.data) {
      return response.data.data;
    }
    return null;
  }

  function loadData() {
    setLoading(true);

    billingServices
      .getCurrentSubscription()
      .then((response) => {
        const data = getPayload(response);
        setSubscription(data);

        // Депозит и счета привязаны к юр. лицу подписки.
        // Если подписки нет — запросы не выполняем.
        const legalEntityId = data && data.legalEntityId;
        const depositPromise = legalEntityId
          ? billingServices.getDeposit(legalEntityId)
          : Promise.resolve(null);

        const invoicesPromise = legalEntityId
          ? billingServices.getInvoices(legalEntityId)
          : Promise.resolve(null);

        return Promise.all([depositPromise, invoicesPromise]);
      })
      .then(([depositResponse, invoicesResponse]) => {
        if (depositResponse) {
          setDeposit(getPayload(depositResponse));
        }
        setInvoices(getPayload(invoicesResponse) || []);
      })
      .catch((err) => {
        const description = typeof err === 'string'
          ? err
          : (err && err.message ? err.message : err);
        notification.error({
          message: i18n.t('components.notification.titles.subscription'),
          description: description,
        });
      })
      .finally(() => setLoading(false));
  }

  function formatAmount(value) {
    if (value === null || value === undefined) {
      return '—';
    }
    return Number(value).toLocaleString('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + ' ₽';
  }

  // Аналог nullish coalescing (??), который запрещён соглашениями проекта
  function or(value, fallback) {
    return value === null || value === undefined ? fallback : value;
  }

  // Текущая стоимость подписки: кастомная цена, если задана, иначе цена тарифа
  function getCurrentPrice(subscription) {
    return or(subscription.customPrice, subscription.price);
  }

  // Количество дней до окончания подписки. Дата окончания приходит с бэка
  // в формате d.m.Y. Округляем вверх: остаток дня считается за полный день.
  function getDaysUntilEnd(subscription) {
    if (!subscription || !subscription.dateEnd) {
      return 0;
    }
    const endDate = moment(subscription.dateEnd, 'DD.MM.YYYY');
    if (!endDate.isValid()) {
      return 0;
    }
    const now = moment().startOf('day');
    const diff = endDate.endOf('day').diff(now, 'days');
    return diff < 0 ? 0 : diff;
  }

  // Уведомления над карточкой подписки, формируемые по условиям:
  //  - isActive=false && isWorking=false -> "Доступ к платформе приостановлен"
  //  - isActive=false (всегда)           -> "Срок действия вашей подписки истёк..."
  //  - депозит < стоимости подписки      -> "Пожалуйста, пополните баланс..."
  function buildSubscriptionAlerts(subscription, deposit) {
    const alerts = [];
    if (!subscription) {
      return alerts;
    }

    const isActive = subscription.isActive;
    const isWorking = subscription.isWorking;

    // 1. Подписка полностью остановлена
    if (!isActive && !isWorking) {
      alerts.push({
        type: 'error',
        message: 'Доступ к платформе приостановлен',
        description: '  ',
      });
    }

    // 2. Срок подписки истёк (показывается всегда при isActive=false)
    if (!isActive) {
      alerts.push({
        type: 'warning',
        message: 'Срок действия вашей подписки истёк',
        description: 'Чтобы продолжить пользоваться сервисом, необходимо пополнить баланс лицевого счета. После поступления оплаты доступ будет восстановлен автоматически.',
      });
    }

    // 3. На депозите меньше средств, чем стоит подписка
    const currentPrice = getCurrentPrice(subscription);
    const depositAmount = deposit && deposit.amount !== null && deposit.amount !== undefined
      ? Number(deposit.amount)
      : null;

    if (depositAmount !== null && depositAmount < Number(currentPrice)) {
      const daysLeft = getDaysUntilEnd(subscription);
      alerts.push({
        type: 'warning',
        message: 'Пожалуйста, пополните баланс',
        description: 'До окончания оплаченного периода осталось ' + daysLeft + ' ' + pluralizeDays(daysLeft) + '. Чтобы работа на платформе не прерывалась, рекомендуем пополнить лицевой счёт заранее.',
      });
    }

    return alerts;
  }

  // Склонение слова "день" по количеству
  function pluralizeDays(n) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) {
      return 'день';
    }
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
      return 'дня';
    }
    return 'дней';
  }

  return (
    <React.Fragment>
      <Spinner loading={loading} tip={i18n.t('main.loadText.loading')}>
        <Row gutter={[16, 16]} style={{ marginBottom: "25px" }}>
          <Col className={"content-header"} span={24}>
            {i18n.t('consignorPage.subscription.title')}
          </Col>
        </Row>

        {/* Уведомления о состоянии подписки */}
        {subscription && buildSubscriptionAlerts(subscription, deposit).map((alert, idx) => (
          <Alert
            key={idx}
            type={alert.type}
            message={alert.message}
            description={alert.description}
            showIcon
            style={{ marginBottom: 16 }}
          />
        ))}

        {/* Текущее состояние подписки */}
        <Card
          title={i18n.t('consignorPage.subscription.subscriptionCard.title')}
          style={{ marginBottom: 16 }}
          extra={subscription ? (
            <Button
              icon={<ClockCircleOutlined/>}
              onClick={() => setIsShowSubscriptionHistory(true)}
            >
              История
            </Button>
          ) : null}
        >
          {subscription ? (
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item
                label={i18n.t('consignorPage.subscription.subscriptionCard.status')}>
                {subscription.isActive
                  ? <Tag color="green">{i18n.t('consignorPage.subscription.subscriptionCard.statusActive')}</Tag>
                  : (subscription.isWorking ? <Tag color="orange">Необходимо продление</Tag> : <Tag color="red">{i18n.t('consignorPage.subscription.subscriptionCard.statusInactive')}</Tag>)}
              </Descriptions.Item>
              <Descriptions.Item
                label={i18n.t('consignorPage.subscription.subscriptionCard.tariff')}>
                {subscription.tariffName}
              </Descriptions.Item>
              <Descriptions.Item
                label={i18n.t('consignorPage.subscription.subscriptionCard.type')}>
                {or(subscription.typeName, subscription.type)}
              </Descriptions.Item>
              <Descriptions.Item
                label={i18n.t('consignorPage.subscription.subscriptionCard.price')}>
                {formatAmount(or(subscription.customPrice, subscription.price))}
              </Descriptions.Item>
              <Descriptions.Item
                label={i18n.t('consignorPage.subscription.subscriptionCard.dateStart')}>
                {or(subscription.dateStart, '—')}
              </Descriptions.Item>
              <Descriptions.Item
                label={i18n.t('consignorPage.subscription.subscriptionCard.dateEnd')}>
                {or(subscription.dateEnd, '—')}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <div>{i18n.t('consignorPage.subscription.subscriptionCard.empty')}</div>
          )}
        </Card>

        <ModalSubscriptionHistory
          subscriptionId={subscription ? subscription.id : null}
          isShow={isShowSubscriptionHistory}
          setIsShow={setIsShowSubscriptionHistory}
        />

        {/* Текущее состояние депозита */}
        <Card
          title={i18n.t('consignorPage.subscription.depositCard.title')}
          extra={deposit ? (
            <Button
              icon={<ClockCircleOutlined/>}
              onClick={() => setIsShowDepositHistory(true)}
            >
              История
            </Button>
          ) : null}
        >
          {deposit ? (
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item
                label={i18n.t('consignorPage.subscription.depositCard.amount')}>
                {formatAmount(deposit.amount)}
              </Descriptions.Item>
              <Descriptions.Item
                label={i18n.t('consignorPage.subscription.depositCard.createdAt')}>
                {or(deposit.createdAt, '—')}
              </Descriptions.Item>
              <Descriptions.Item
                label={i18n.t('consignorPage.subscription.depositCard.updatedAt')}>
                {or(deposit.updatedAt, '—')}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <div>{i18n.t('consignorPage.subscription.depositCard.empty')}</div>
          )}
        </Card>

        <ModalDepositHistory
          depositId={deposit ? deposit.id : null}
          isShow={isShowDepositHistory}
          setIsShow={setIsShowDepositHistory}
        />

        {/* Активные (неоплаченные) счета */}
        <Card title={i18n.t('consignorPage.subscription.invoicesCard.title')}
              style={{ marginTop: 16 }}
              extra={subscription && subscription.legalEntityId ? (
                <Button
                  icon={<ClockCircleOutlined/>}
                  onClick={() => setIsShowInvoicesHistory(true)}
                >
                  История
                </Button>
              ) : null}>
          <Table
            rowKey="id"
            dataSource={invoices}
            pagination={false}
            size="small"
            locale={{ emptyText: i18n.t('consignorPage.subscription.invoicesCard.empty') }}
            columns={[
              {
                title: i18n.t('consignorPage.subscription.invoicesCard.number'),
                dataIndex: 'number',
                key: 'number',
              },
              {
                title: i18n.t('consignorPage.subscription.invoicesCard.amount'),
                dataIndex: 'amount',
                key: 'amount',
                render: (value) => formatAmount(value),
              },
              {
                title: i18n.t('consignorPage.subscription.invoicesCard.status'),
                dataIndex: 'status',
                key: 'status',
                render: (value) => (
                  <Tag color="orange">
                    {i18n.t('consignorPage.subscription.invoicesCard.statusPending')}
                  </Tag>
                ),
              },
              {
                title: i18n.t('consignorPage.subscription.invoicesCard.createdAt'),
                dataIndex: 'createdAt',
                key: 'createdAt',
                render: (value) => or(value, '—'),
              },
              {
                title: '',
                key: 'download',
                render: (_, record) => (
                  <Button
                    type="link"
                    size="small"
                    href={record.fileUrl}
                    target="_blank"
                    disabled={!record.fileUrl}
                  >
                    {i18n.t('consignorPage.subscription.invoicesCard.download')}
                  </Button>
                ),
              },
            ]}
          />
        </Card>

        <ModalInvoicesHistory
          legalEntityId={subscription ? subscription.legalEntityId : null}
          isShow={isShowInvoicesHistory}
          setIsShow={setIsShowInvoicesHistory}
        />
      </Spinner>
    </React.Fragment>
  );
}
